import { Component } from '@angular/core';
import { asapScheduler, BehaviorSubject, combineLatest, filter, map, Observable, throttleTime } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ComparisonUtility } from '@utilities/comparison.utility';
import { ProcessingManager } from '@managers/processing.manager';
import { TableLikeBaseComponent } from '../../../table-like-base.component';
import { Server } from '@models/ui/server.model';
import { Sort } from '@angular/material/sort';
import { orderBy } from 'lodash-es';
import { Clipboard } from '@angular/cdk/clipboard';
import { NotificationManager } from '@managers/notification.manager';
import { MatDialog } from '@angular/material/dialog';
import { ServiceScalingDialogComponent } from "@dialogs/service-scaling/service-scaling-dialog.component";
import { AdjustServiceCountRequest, PlatformClient, ServiceIdentifier } from "@clients/api.client";
import { rowTransitionAnimation } from "../animations";

@Component({
    selector: 'lex-microservices',
    templateUrl: './services-table.component.html',
    animations: [rowTransitionAnimation],
    styleUrls: ['./services-table.component.scss'],
    standalone: false
})
export class ServicesTableComponent extends TableLikeBaseComponent<Server> {
	public dataSource$: Observable<MatTableDataSource<Server>>;
	public rowHeight$ = this.processingManager.currentRowHeight$;
	public sort$ = new BehaviorSubject<Sort>({active: 'host', direction: 'desc'});
	public columnsToDisplay = [
		'status',
		'serviceCounts',
		'serviceName',
		'assemblyVersions',
		'currentOperations',
		'settings'
	];

	constructor(
		public processingManager: ProcessingManager,
		public clipboard: Clipboard,
		public notificationManager: NotificationManager,
		public platformClient: PlatformClient,
		private dialog: MatDialog
	) {
		super();

		this.dataSource$ = combineLatest([
			this.processingManager.ms$.pipe(
				throttleTime(450, asapScheduler, {
					leading: true,
					trailing: true,
				})
			),
			this.processingManager.currentFilterText$,
			this.sort$
		]).pipe(
			filter(([collection]) => !!collection),
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map(([serviceCollection]) => {
				serviceCollection = orderBy(serviceCollection, ['totalServiceCount', 'serviceName'], 'desc');
				return new MatTableDataSource(serviceCollection as any);
			})
		);
	}

	public trackByFn(index: number, item: any): string {
		return `${item.applicationContextId}`;
	}

	public openServiceScalingDialog(row: any): void {
		const dialogRef = this.dialog.open(ServiceScalingDialogComponent, {
			width: '400px',
			data: {maxCount: row.totalServiceCount}
		});
		dialogRef.afterClosed().pipe(
			switchMap(result => {
				if (result) {
					const request = new AdjustServiceCountRequest({
						desiredCount: result.maxCount,
						identifier: new ServiceIdentifier({
							clusterName: row.ecsClusterName,
							serviceName: row.ecsServiceName
						}),
					});
					return this.platformClient.setServiceDesiredCount$(request);
				}
				return [];
			})
		).subscribe();
	}

	public refreshServices(row: any): void {
		const body = new ServiceIdentifier({clusterName: row.ecsClusterName, serviceName: row.ecsServiceName});

		this.platformClient.refreshService$(body).subscribe(() => {
				this.notificationManager.displaySnackBarMessage({
					message: 'Refreshing service: ' + row.serviceName
				});
			}
		);
	}
}
